/* SKN&B gift guard — auto-adds/removes the free Gua Sha (GWP).
 *
 * The admin automatic "Buy X Get Y" discount (min spend -> gift 100% off) never
 * adds the gift to the cart by itself (Shopify: the get-item "is never
 * automatically added to the cart"). This script owns that lifecycle:
 *   - eligible subtotal (cart minus gift lines) >= threshold  -> add the gift
 *   - our gift line exists but is NOT free (dropped below threshold, or a
 *     currency-rate mismatch made the add premature) -> remove it, so the
 *     customer is never silently charged for a "free" gift
 *   - after adding, re-read the cart and verify the discount actually zeroed
 *     the line; roll back immediately if it didn't (self-correcting in EUR,
 *     where the threshold is a rate conversion)
 *   - if the customer manually deletes the gift, respect it for the session
 * Config comes from [data-sknb-gift] attributes rendered by the reward bar /
 * routine section. Thresholds are in cents; non-SEK uses Shopify.currency.rate
 * with the Liquid-derived presentment threshold as fallback.
 */
(function () {
  // header-actions renders in both header variants, so this file can be included twice.
  // Two instances raced each other and both added a gift (one free, one charged).
  if (window.__sknbGiftGuard) return;
  window.__sknbGiftGuard = true;

  var OPTOUT_KEY = 'sknb_gift_optout_v1';
  var SEEN_KEY = 'sknb_gift_seen_v1';
  var SOURCE = 'sknb-gift';
  var busy = false;
  var timer = null;

  function ss(action, key, value) {
    try {
      if (action === 'get') return sessionStorage.getItem(key);
      if (action === 'set') sessionStorage.setItem(key, value);
      if (action === 'del') sessionStorage.removeItem(key);
    } catch (e) {}
    return null;
  }

  function config() {
    return document.querySelector('[data-sknb-gift]');
  }

  function threshold(g, currency) {
    if (!g.sek) return 0;
    if (currency === 'SEK') return g.sek;
    var rate = window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate);
    if (rate && rate > 0) return Math.round(g.sek * rate);
    return g.presentment || 0;
  }

  // Gift definitions from the reward bar. Both stages qualify on the cart minus BOTH gift
  // products (data-gift-exclude), mirroring the discounts: the Gua Sha Buy X Get Y uses a
  // collection without either gift, and the Heatless app discount excludes both.
  function gifts() {
    var cfg = config();
    if (!cfg) return [];
    var list = [];
    var h1 = cfg.getAttribute('data-gift-handle');
    var v1 = parseInt(cfg.getAttribute('data-gift-variant'), 10);
    if (h1 && v1) {
      list.push({
        tag: '1', handle: h1, variant: v1,
        sek: parseInt(cfg.getAttribute('data-gift-threshold-sek'), 10) || 0,
        presentment: parseInt(cfg.getAttribute('data-gift-threshold-presentment'), 10) || 0,
        exclude: (cfg.getAttribute('data-gift-exclude') || h1).split(','), optout: OPTOUT_KEY, seen: SEEN_KEY,
      });
    }
    var c2 = document.querySelector('[data-gift2-variant]');
    var h2 = c2 && c2.getAttribute('data-gift2-handle');
    var v2 = c2 && parseInt(c2.getAttribute('data-gift2-variant'), 10);
    if (h1 && h2 && v2) {
      list.push({
        tag: '2', handle: h2, variant: v2,
        sek: parseInt(c2.getAttribute('data-gift2-threshold-sek'), 10) || 0,
        presentment: parseInt(c2.getAttribute('data-gift2-threshold-presentment'), 10) || 0,
        exclude: [h1, h2], optout: 'sknb_gift2_optout_v1', seen: 'sknb_gift2_seen_v1',
      });
    }
    return list;
  }

  function notify(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cart, sourceId: SOURCE, data: { source: SOURCE, itemCount: cart.item_count } },
      })
    );
  }

  function getCart() {
    return fetch('/cart.js').then(function (r) { return r.ok ? r.json() : null; });
  }

  function removeLine(line) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: line.key, quantity: 0 }),
    });
  }

  // Returns true when the cart was changed (caller re-reads the cart).
  async function syncGift(cart, g) {
    var thr = threshold(g, cart.currency);
    if (!thr) return false;

    var giftLines = cart.items.filter(function (i) { return i.handle === g.handle; });
    var eligible = cart.items.reduce(function (sum, i) {
      return g.exclude.indexOf(i.handle) !== -1 ? sum : sum + i.final_line_price;
    }, 0);
    var ourLines = giftLines.filter(function (i) { return i.properties && i.properties._sknb_gift; });
    var ourPaidGift = ourLines.filter(function (i) { return i.final_line_price > 0; })[0];
    if (!ourPaidGift && ourLines.length > 1) {
      // More than one auto-added gift (all free) — keep one.
      ourPaidGift = ourLines[ourLines.length - 1];
    }
    // Track ANY line of this gift product (ours or e.g. the routine builder's): if the
    // customer had one and deleted it themselves, respect that for the session
    // instead of bouncing it back in.
    var giftPresent = giftLines.length > 0;
    if (!giftPresent && ss('get', g.seen) === '1') {
      ss('del', g.seen);
      ss('set', g.optout, '1');
    }
    if (giftPresent) ss('set', g.seen, '1');

    if (ourPaidGift) {
      // Below threshold, premature add or a duplicate: never charge for the "gift".
      ss('del', g.seen);
      await removeLine(ourPaidGift);
      return true;
    }

    if (ss('get', g.optout) === '1') return false;

    if (eligible >= thr && giftLines.length === 0) {
      var res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: g.variant, quantity: 1, properties: { _sknb_gift: g.tag } }] }),
      });
      if (!res.ok) return false; // e.g. sold out — fail quietly, bar copy stays honest
      var cart2 = await getCart();
      if (!cart2) return true;
      var paidAdded = cart2.items.filter(function (i) {
        return i.handle === g.handle && i.properties && i.properties._sknb_gift && i.final_line_price > 0;
      });
      if (paidAdded.length) {
        // Discount did not apply (rate drift) or a duplicate slipped in — roll back the charged line(s).
        for (var p = 0; p < paidAdded.length; p++) await removeLine(paidAdded[p]);
        ss('del', g.seen);
        return 'rolled-back';
      }
      ss('set', g.seen, '1');
      return true;
    }
    return false;
  }

  async function sync() {
    if (busy) return;
    var list = gifts();
    if (!list.length) return;
    busy = true;
    var changed = false;
    var rolledBack = false;
    try {
      for (var n = 0; n < list.length; n++) {
        var cart = await getCart();
        if (!cart) return;
        var r = await syncGift(cart, list[n]);
        if (r) changed = true;
        if (r === 'rolled-back') rolledBack = true;
      }
    } catch (e) {
    } finally {
      busy = false;
    }
    if (changed) {
      var after = await getCart();
      if (after) notify(after);
      // A removal can change the other gift's qualification — re-check once.
      // Skip after a rate-drift rollback so a non-applying discount can't loop.
      if (!rolledBack) schedule();
    }
  }

  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () { timer = null; sync(); }, 250);
  }

  document.addEventListener('cart:update', function (e) {
    var src = e && e.detail && e.detail.data && e.detail.data.source;
    if (src === SOURCE) return;
    schedule();
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
  window.addEventListener('pageshow', schedule);
})();
